import { Loader } from '../../components/Loader';
import { NumberRegistration } from '../../components/NumberAcquisition';
import { NumberRegistrationComponent } from '../../components/Registrations';
import { VoicemailManagement } from '../../components/VoicemailManagement';
import { UserSettings } from '../../components/UserSettings/UserSettings';
import { TeamManagement } from '../../components/TeamManagement';
import { RoutingManagement } from '../../components/RoutingManagement';
import { Typography } from 'interfaces/typography';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { AIGeneration } from '../../components/AIGeneration';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { useTeamAccountType } from '../../lib/redux/users/team-account-type-hook';
import { LinkedInManagement } from '../../components/LinkedInManagement';
import { Invoices } from '../../components/Invoice/Invoices';
import { AutodialerSettingsV2 } from 'components/AutodialerSettings/AutodialerSettings';
import { RequiresAdminAccess, RequiresPaidAccess, RequiresParallelAccess } from '../../utils/CommonUIComponents';
import { Integrations } from '../../components/Integrations';
import { APIDocumentation } from '../../components/APIDocumentation';
import { Compass } from 'lucide-react';

export enum SettingsPages {
    USER = 'User',
    TEAM_MANAGEMENT = 'Team',
    AUTODIALER_SETTINGS = 'Dialer',
    INTEGRATIONS = 'Integrations',
    NUMBERS = 'Numbers',
    ROUTING = 'Routing',
    SPAM_REDUCTION = 'Spam Reduction',
    CUSTOM_SCORES = 'AI Customizations',
    LINKEDIN = 'LinkedIn',
    VOICEMAILS = 'Voicemails',
    INVOICES = 'Billing',
    API = 'API',
}

export type SettingsProps = {
    defaultSettings?: SettingsPages
}

export function Settings({ defaultSettings }: SettingsProps) {
  const { userLoaded } = useUserData()
  const adminLoaded = useSelector((state: RootState) => state.adminWrite.value !== null)
  const { isLegal } = useTeamAccountType()
  
  const [activeSettingPage, setActiveTab] = useState<SettingsPages>(
    defaultSettings ?? (isLegal ? SettingsPages.TEAM_MANAGEMENT : SettingsPages.USER)
  )
  const navigate = useNavigate()
  const location = useLocation()

  const getSettingOptions = () => {
    if (isLegal) {
      // For legal users, show only Team management
      return [
        SettingsPages.TEAM_MANAGEMENT,
      ]
    }
    
    // Show all options - access control is handled by the wrapper components
    const options = [
      SettingsPages.USER,
      SettingsPages.TEAM_MANAGEMENT,
      SettingsPages.AUTODIALER_SETTINGS,
      SettingsPages.INTEGRATIONS,
      SettingsPages.NUMBERS,
      SettingsPages.ROUTING,
      SettingsPages.SPAM_REDUCTION,
      SettingsPages.CUSTOM_SCORES,
      SettingsPages.LINKEDIN,
      SettingsPages.VOICEMAILS,
      SettingsPages.INVOICES,
      SettingsPages.API
    ]
    return options
  }

  const renderContent = () => {
    switch (activeSettingPage) {
      case SettingsPages.USER:
        return <UserSettings />
      case SettingsPages.AUTODIALER_SETTINGS:
        return <AutodialerSettingsV2 />
      case SettingsPages.CUSTOM_SCORES:
        return (
          <RequiresAdminAccess>
            <RequiresPaidAccess>
              <AIGeneration />
            </RequiresPaidAccess>
          </RequiresAdminAccess>
        )
      case SettingsPages.TEAM_MANAGEMENT:
        return (
          <RequiresAdminAccess>
            <TeamManagement />
          </RequiresAdminAccess>
        )
      case SettingsPages.NUMBERS:
        return (
          <RequiresPaidAccess>
          <RequiresParallelAccess>
            <NumberRegistration />
          </RequiresParallelAccess>
          </RequiresPaidAccess>
        )
      case SettingsPages.ROUTING:
        return (
          <RequiresPaidAccess>
          <RequiresParallelAccess>
            <RoutingManagement />
          </RequiresParallelAccess>
          </RequiresPaidAccess>
        )
      case SettingsPages.SPAM_REDUCTION:
        return (
          <RequiresPaidAccess>
          <RequiresAdminAccess>
            <RequiresParallelAccess>
              <NumberRegistrationComponent />
            </RequiresParallelAccess>
          </RequiresAdminAccess>
          </RequiresPaidAccess>
          )
      case SettingsPages.VOICEMAILS:
        return (
          <RequiresPaidAccess>
            <RequiresParallelAccess>
              <VoicemailManagement />
            </RequiresParallelAccess>
          </RequiresPaidAccess>
        )
      case SettingsPages.LINKEDIN:
        return <LinkedInManagement />
      case SettingsPages.INVOICES:
        return (
          <RequiresPaidAccess>
            <Invoices />
          </RequiresPaidAccess>
        )
      case SettingsPages.INTEGRATIONS:
        return <Integrations />
      case SettingsPages.API:
        return (
          <RequiresAdminAccess>
            <RequiresPaidAccess>
              <RequiresParallelAccess>
                <APIDocumentation />
              </RequiresParallelAccess>
            </RequiresPaidAccess>
          </RequiresAdminAccess>
        )
      default:
        return (
          <div className="p-6">
            <Typography variant="h3" color="#374151">
              Select a setting option to configure
            </Typography>
          </div>
        )
    }
  }

  const updateSelectedSettings = (id: SettingsPages) => {
    if (!id) return
    if (id === activeSettingPage) return
    
    // Only check for unsaved changes when navigating between tabs
    const customScoresElement = document.querySelector('[role="tabpanel"]')
    if (activeSettingPage === SettingsPages.CUSTOM_SCORES && customScoresElement) {
      const hasChanges = customScoresElement.getAttribute('data-has-changes') === 'true'
      if (hasChanges) {
        const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?')
        if (!shouldLeave) {
          return
        }
      }
    }
    
    navigate(`/settings?tab=${id}`)
  }

  // Map old tab names to new ones for backward compatibility
  const mapOldTabToNew = (tab: string): SettingsPages | null => {
    const oldToNewMapping: Record<string, SettingsPages> = {
      'General': SettingsPages.USER,
      'Users': SettingsPages.TEAM_MANAGEMENT,
      'Subteams': SettingsPages.TEAM_MANAGEMENT,
      'Invites': SettingsPages.TEAM_MANAGEMENT,
      'LinkedIn Snippets': SettingsPages.LINKEDIN,
      'LinkedIn Voice Notes': SettingsPages.LINKEDIN,
      'Registrations': SettingsPages.SPAM_REDUCTION,
      'Inbound Routing': SettingsPages.USER, // Redirect removed page to User
      'Voicemail Drop': SettingsPages.VOICEMAILS,
    };

    return oldToNewMapping[tab] || (Object.values(SettingsPages).includes(tab as SettingsPages) ? tab as SettingsPages : null);
  };

  const getCurrentId = useCallback(() => {
    const tabParam = new URLSearchParams(location.search).get("tab");
    if (!tabParam) return null;
    return mapOldTabToNew(tabParam);
  }, [location])
  
  useEffect(() => {
    const pageSessionId = getCurrentId()
    if (!pageSessionId) return
    // Don't show confirmation when URL changes externally
    setActiveTab(pageSessionId)
  }, [location, getCurrentId])

  const renderOption = (settingPage: SettingsPages) => {
    return (
      <button
        key={settingPage}
        onClick={() => updateSelectedSettings(settingPage)}
        className={`
          w-full px-2 py-2.5 text-left transition-colors duration-200 relative border-l-2
          ${
            activeSettingPage === settingPage
              ? "bg-emerald-50 border-emerald-500"
              : "hover:bg-gray-50 border-transparent"
          }
        `}
      >
        <Typography 
          variant="mediumParagraph" 
          color={activeSettingPage === settingPage ? "#059669" : "#374151"}
          fontWeight={activeSettingPage === settingPage ? 500 : 400}
        >
          {settingPage}
        </Typography>
      </button>
    )
  }

  if (!userLoaded || !adminLoaded) {
    return <Loader />
  }

  return (
    <div className="h-full w-full flex bg-gray-50">
      {/* Sidebar */}
      <div className="w-36 bg-white border-r border-gray-200 flex-shrink-0 shadow-sm">
        <div className="px-2 py-4 border-b border-gray-200">
          <Typography variant="h4" color="#1F2937" fontWeight={600}>
            Settings
          </Typography>
        </div>
        <nav className="flex-1 overflow-y-auto">
          <div className="py-2">
            {getSettingOptions().map((settingPage) => renderOption(settingPage))}
          </div>

          {/* Onboarding Guide Link */}
          <div className="border-t border-gray-200 mt-2 pt-2">
            <button
              onClick={() => navigate('/onboarding')}
              className="w-full px-2 py-2.5 text-left transition-colors duration-200 hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                <Compass className="w-4 h-4 text-gray-600" />
                <Typography
                  variant="mediumParagraph"
                  color="#374151"
                  fontWeight={400}
                >
                  Onboarding
                </Typography>
              </div>
            </button>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  )
}